import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BadgeCheck, Undo2 } from "lucide-react";
import { fmtDate } from "@/lib/timefmt";
import MembershipSetup from "@/components/MembershipSetup";
import TaskReviewDialog from "@/components/TaskReviewDialog";

export default function TaskAuthPage() {
  const { user } = useAuth();
  const taskLabel = user?.terminology?.task_label || "Task";
  const tasksLabel = user?.terminology?.task_label_plural || "Tasks";
  const [data, setData] = useState({ pending: [], authorised_by_me: [], is_auth_officer: false });
  const [showAuthored, setShowAuthored] = useState(false);
  const [busy, setBusy] = useState("");
  const [review, setReview] = useState(null);

  const load = () => api.get("/tasks/pending-auth").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (t, on) => {
    setBusy(t.id);
    try {
      await api.post(`/tasks/${t.id}/authorise`, { on });
      toast.success(on ? `${taskLabel} authorised` : "Authorisation revoked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!user?.company_id) {
    return <MembershipSetup title={`${taskLabel} Authorisation`} message="Join a company to see tasks awaiting authorisation." />;
  }

  const visible = data.pending.filter((t) => t.mine || (data.is_auth_officer && t.in_scope));

  return (
    <div className="max-w-4xl" data-testid="task-auth-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{taskLabel} Authorisation</h1>
        <p className="text-slate-400 mt-1 text-sm">
          Committed {tasksLabel.toLowerCase()} awaiting an Auth Officer's sign-off.
          {data.is_auth_officer
            ? " As an Auth Officer you can authorise tasks inside each type's configured scope."
            : " You can track your own tasks here; only configured Auth Officers can authorise."}
        </p>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-5">
        <div className="font-heading text-lg font-semibold mb-3">Awaiting authorisation ({visible.length})</div>
        {visible.length === 0 && <div className="text-sm text-slate-500" data-testid="task-auth-empty">Nothing awaiting authorisation.</div>}
        <div className="space-y-2" data-testid="task-auth-list">
          {visible.map((t) => (
            <div key={t.id} className="p-4 rounded-lg border border-slate-800 bg-slate-950/40 flex items-start justify-between gap-3 flex-wrap" data-testid={`task-auth-card-${t.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{t.name || t.task_type_name}</span>
                  {t.name && <span className="text-[10px] text-slate-500">{t.task_type_name}</span>}
                  <span className="text-xs font-mono-num text-slate-400">{fmtDate(t.date)} · {t.start}–{t.end}</span>
                  {t.mine && <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-widest bg-blue-600/15 text-blue-300 border border-blue-600/40">Your {taskLabel.toLowerCase()}</span>}
                  {t.in_scope && <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-widest bg-violet-600/15 text-violet-300 border border-violet-600/40">In your scope</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  by {t.created_by_name}{(t.participants || []).length ? ` · with ${t.participants.map((p) => p.name).join(", ")}` : ""}
                </div>
                {t.remarks && <div className="text-[11px] text-slate-400 mt-1 italic">{t.remarks}</div>}
                {t.auth_comments && <div className="text-[11px] text-violet-300/80 mt-1" data-testid={`task-auth-comments-${t.id}`}>Auth comments: {t.auth_comments}</div>}
                {t.auth_restrictions && <div className="text-[11px] text-amber-300/80 mt-1" data-testid={`task-auth-restrictions-${t.id}`}>Restrictions: {t.auth_restrictions}</div>}
              </div>
              {t.in_scope && (
                <button onClick={() => setReview(t)} data-testid={`task-auth-authorise-${t.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-violet-600/20 hover:bg-violet-600/30 border border-violet-600/50 text-violet-200 text-xs font-medium transition-colors self-center shrink-0">
                  <BadgeCheck className="h-3.5 w-3.5" /> Review &amp; authorise
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {data.is_auth_officer && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
          <button onClick={() => setShowAuthored((v) => !v)} data-testid="task-auth-show-authored"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
            <BadgeCheck className="h-4 w-4 text-violet-300" />
            <span className="font-heading text-base font-semibold">Authorised by me — awaiting post-{taskLabel.toLowerCase()} ({data.authorised_by_me.length})</span>
          </button>
          {showAuthored && (
            <div className="space-y-2 mt-3" data-testid="task-auth-authored-list">
              {data.authorised_by_me.length === 0 && <div className="text-sm text-slate-500">None outstanding.</div>}
              {data.authorised_by_me.map((t) => (
                <div key={t.id} className="p-4 rounded-lg border border-violet-600/30 bg-violet-600/5 flex items-start justify-between gap-3 flex-wrap" data-testid={`task-auth-authored-${t.id}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{t.name || t.task_type_name}</span>
                      <span className="text-xs font-mono-num text-slate-400">{fmtDate(t.date)} · {t.start}–{t.end}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">by {t.created_by_name}</div>
                  </div>
                  <button onClick={() => act(t, false)} disabled={busy === t.id} data-testid={`task-auth-revoke-${t.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-amber-900/40 text-slate-300 hover:text-amber-300 text-xs font-medium transition-colors disabled:opacity-50 self-center shrink-0">
                    <Undo2 className="h-3.5 w-3.5" /> Revoke authorisation
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {review && <TaskReviewDialog task={review} onClose={() => setReview(null)} onChanged={load} />}
    </div>
  );
}
